import { DatePipe, NgIf } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DialogType, FontFamilyModel, FontSizeModel, HtmlEditorService, ImageService, LinkService, RichTextEditor, RichTextEditorComponent, RichTextEditorModule, ToolbarService, ToolbarSettingsModel } from '@syncfusion/ej2-angular-richtexteditor';
import { FileAttachmentService } from '../../services/file-attachment.service';
import { MessageStackService } from '../../services/message-stack.service';
import { TicketService } from '../../services/ticket.service';

/**
 * Renders the email conversation on a ticket and the inline reply editor.
 *
 * The thread is not fetched here: {@link HelpDeskComponent} loads it and parks it
 * on `MessageStackService`, which this component drains in `ngOnInit`.
 *
 * Each message body is passed through `bypassSecurityTrustHtml`, so the Angular
 * sanitiser is deliberately bypassed and the backend is trusted to have cleaned
 * the stored email HTML.
 */
@Component({
  selector: 'app-message-stack',
  imports: [RichTextEditorModule, NgIf, FormsModule],
  providers: [ToolbarService, LinkService, ImageService, HtmlEditorService, DatePipe],
  templateUrl: './message-stack.component.html',
  styleUrl: './message-stack.component.css'
})

export class MessageStackComponent implements OnInit, OnDestroy {
  /** Whether the standalone reply box is open; per-message replies use `reply_box_flag`. */
  replyBox: boolean = false
  /** Rich text editor instance backing the reply box. */
  @ViewChild('rte') public rteObj!: RichTextEditorComponent;
  /** Guards against binding the editor paste/drop handlers more than once. */
  private listenersAdded = false;
  
  // Current user information
  /** Display name of the signed-in user, read from `sessionStorage`. */
  currentUserName: string = '';
  /** Email address of the signed-in user, read from `sessionStorage`. */
  currentUserEmail: string = '';

  /** Editor image settings; tags inserted images so component CSS can target them. */
  public insertImageSettings = {
    // allowedTypes: ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.webp'],
    // display: 'inline',
    // width: 'auto',
    // height: 'auto',
    cssClass: 'uploaded-image'
  };

  
  /** Editor instance placeholder retained for template binding. */
  defaultRTE!: RichTextEditor;
  /** Toolbar layout for the reply editor. */
  public tools: ToolbarSettingsModel = {
    items: [
      'Bold', 'Italic', 'Underline', 'StrikeThrough', '|',
      'FontName', 'FontSize', 'FontColor', 'BackgroundColor', '|',
      'LowerCase', 'UpperCase', '|',
      'SuperScript', 'SubScript', '|',
      'Alignments', '|',
      'OrderedList', 'UnorderedList', '|',
      'Indent', 'Outdent', '|',
      'CreateLink', 'Image', '|',
      'CreateTable', '|',
      'ClearFormat', 'Print', '|',
      'Undo', 'Redo', '|',
      'FullScreen'
    ], 
  };

  /**
   * Font list for the reply editor's toolbar.
   *
   * The design (Figma node 6078:79128) shows the editor composing in Poppins,
   * with the toolbar reading "Poppins" rather than the generic "Font Name".
   * Syncfusion only labels that dropdown with a value it can find in its own
   * item list, so Poppins is added to the list as well as set as the default -
   * setting `default` alone leaves the placeholder showing.
   */
  public fontFamily: FontFamilyModel = {
    default: 'Poppins',
    items: [
      { text: 'Poppins', value: 'Poppins' },
      { text: 'Segoe UI', value: 'Segoe UI' },
      { text: 'Arial', value: 'Arial, Helvetica, sans-serif' },
      { text: 'Georgia', value: 'Georgia, serif' },
      { text: 'Times New Roman', value: 'Times New Roman, Times, serif' },
      { text: 'Verdana', value: 'Verdana, Geneva, sans-serif' }
    ]
  };

  /** Matching 12pt default, so the size dropdown reads "12 pt" as drawn. */
  public fontSize: FontSizeModel = {
    default: '12pt'
  };
  /** Reply body being composed, as HTML from the rich text editor. */
  replyMessage: string = ''
  /** Ticket whose conversation is displayed and replied to. */
  @Input() ticketID!: any
  /** CC recipients carried into the reply. */
  @Input() participantsList: string[] = []
  /** Hidden file input, clicked programmatically by {@link triggerFileInput}. */
  @ViewChild('fileInput') fileInput: any;
  /** Files staged as attachments on the reply. */
  uploadedFilesArray: File[] = [];
  /** Emails in the thread, each decorated with formatted date/time and a reply flag. */
  messageStack: any;
  /** Whether the full thread is shown; collapsed automatically past five messages. */
  messageStackExpanded: boolean = true;
  /** Hover state for the reply button. */
  isButtonHovered: boolean = false;
  /** Scratch slot for a sanitiser-bypassed email body. */
  safeEmailBody: SafeHtml | undefined;
  /**
   * Attachment allow-list mapping file extension to its expected MIME type.
   *
   * Both must agree for a file to be accepted, so a renamed extension is rejected.
   */
  ALLOWED_FILES: { [key: string]: string } = {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    bmp: "image/bmp",
    tiff: "image/tiff",
    tif: "image/tiff",
    webp: "image/webp",
    svg: "image/svg+xml",
    ico: "image/x-icon",
    heic: "image/heic",
    heif: "image/heif",
    jfif: "image/jpeg",
    pjpeg: "image/pjpeg",
    pjp: "image/pjpeg",
    pdf: "application/pdf",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    txt: "text/plain",
    csv: "text/csv",
    ppt: "application/vnd.ms-powerpoint",
    pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    mp3: "audio/mpeg",
    wav: "audio/wav",
    ogg: "audio/ogg",
    aac: "audio/aac",
    flac: "audio/flac",
    m4a: "audio/mp4",
    mp4: "video/mp4",
    avi: "video/x-msvideo",
    mov: "video/quicktime",
    wmv: "video/x-ms-wmv",
    flv: "video/x-flv",
    mkv: "video/x-matroska",
    webm: "video/webm",
    zip: "application/zip",
    rar: "application/vnd.rar",
    "7z": "application/x-7z-compressed",
    tar: "application/x-tar",
    gz: "application/gzip",
  };

  /** Expands a thread that was collapsed for length. */
  expandMessageStack() {
    this.messageStackExpanded = true;
  }

  /** Validation message for attachment type and total-size failures. */
  fileUploadErrorMessage: string = '';

  /**
   * Validates newly picked attachments and appends the acceptable ones.
   *
   * A file is accepted only when its extension appears in {@link ALLOWED_FILES} and
   * its reported MIME type matches. The batch is rejected outright if any file
   * fails, or if the combined size would exceed 25 MB.
   *
   * @param event Change event from the hidden file input.
   */
  handleFileSelect(event: any) {
    this.fileUploadErrorMessage = '';

    const files = Array.from(event.target.files) as File[];
    const maxTotalSize = 25 * 1024 * 1024;

    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    for (const file of files) {
      const ext = file.name.split('.').pop()?.toLowerCase();
      const mime = file.type;

      if (ext && this.ALLOWED_FILES[ext] && this.ALLOWED_FILES[ext] === mime) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    }

    if (invalidFiles.length > 0) {
      this.fileUploadErrorMessage = `These files are not allowed:\n${invalidFiles.join(', ')}`;
      return;
    }

    const currentTotalSize = this.uploadedFilesArray.reduce((sum, file) => sum + file.size, 0);
    const newFilesTotalSize = validFiles.reduce((sum, file) => sum + file.size, 0);
    const combinedTotalSize = currentTotalSize + newFilesTotalSize;

    if (combinedTotalSize > maxTotalSize) {
      this.fileUploadErrorMessage = 'Total attachments size must not exceed 25 MB.';
      return;
    }

    this.uploadedFilesArray = [...this.uploadedFilesArray, ...validFiles];
    this.fileUploadErrorMessage = '';
  }

  /**
   * Sends the composed reply, then reloads the thread so the new message appears.
   *
   * Does nothing while an attachment validation error is outstanding, and does so
   * silently - the button gives no feedback in that case.
   */
  replyToMessage() { 

    if(this.fileUploadErrorMessage === '' ) {
      this.ticketService.replyOnTicket(this.ticketID, this.replyMessage, this.uploadedFilesArray).subscribe({
        next: () => {
          this.ticketService.getTicketDetails(this.ticketID).subscribe({
            next: (response) => {
              this.messageStack = response
              this.messageStack.forEach((email: any) => {
                email.email_formatted_date = this.dateType.transform(email.email_date, 'MMMM dd, yyyy')
                email.email_formatted_time = this.dateType.transform(email.email_date, 'h:mm a')
                email.reply_box_flag = false
                email.email_body = this.sanitizer.bypassSecurityTrustHtml(email.email_body)
              })
            }
          })
          
        },
        error: (error) => {
          console.error(error)
        }
      })
    }
    
  }

  /** Opens the OS file picker by clicking the hidden file input. */
  triggerFileInput() {
    this.fileInput.nativeElement.click();
  }

  /**
   * Toggles the inline reply box under one message, clearing any draft and error.
   *
   * @param email Message whose reply box is being toggled.
   */
  showReplyBox(email: any) {
    this.replyMessage = '';
    this.fileUploadErrorMessage = ''
    const opening = !email.reply_box_flag
    // At most one reply box may be open: the template declares #rte once per
    // branch and @ViewChild('rte') resolves a single instance, so a second open
    // box would leave pasted images and the shared replyMessage draft bound to
    // the wrong editor.
    this.messageStack.forEach((message: any) => { message.reply_box_flag = false })
    email.reply_box_flag = opening
  }

  /**
   * Removes one staged attachment and re-checks the remaining total size.
   *
   * Note: the threshold checked here is 20 MB, while {@link handleFileSelect} adds
   * files against 25 MB and the message shown still reads 25 MB.
   *
   * @param file Attachment to remove.
   */
  removeFileFromUploadedArray(file: File) {
    this.uploadedFilesArray = this.uploadedFilesArray.filter(f => f !== file);

    const maxTotalSize = 20 * 1024 * 1024; 
    const totalSize = this.uploadedFilesArray.reduce((sum, f) => sum + f.size, 0);

    if (totalSize > maxTotalSize) {
      this.fileUploadErrorMessage = 'Total attachments size must not exceed 25 MB.';
    } else {
      this.fileUploadErrorMessage = '';
    }    
  }

  /**
   * Reads the signed-in user, drains the thread from `MessageStackService`, and
   * decorates each message with display date/time, a collapsed reply flag and a
   * sanitiser-bypassed body.
   *
   * Threads longer than five messages start collapsed.
   */
  ngOnInit(): void {
    // Get current user information from sessionStorage
    this.currentUserName = sessionStorage.getItem('userName') || 'Current User';
    this.currentUserEmail = sessionStorage.getItem('emailAddress') || 'currentuser@company.com';
    
    this.messageStack = this.messageStackService.messageStack
    this.messageStack.forEach((email: any) => {
      email.email_formatted_date = this.dateType.transform(email.email_date, 'MMMM dd, yyyy')
      email.email_formatted_time = this.dateType.transform(email.email_date, 'h:mm a')
      email.reply_box_flag = false
      email.email_body = this.sanitizer.bypassSecurityTrustHtml(email.email_body)
    })

    if (this.messageStack.length > 5) {
      this.messageStackExpanded = false;
    }
  }

  constructor( private readonly dateType: DatePipe, private readonly ticketService: TicketService, private readonly messageStackService: MessageStackService, private readonly fileAttachmentService: FileAttachmentService, private readonly sanitizer: DomSanitizer, private http: HttpClient) {
    this.messageStack = []  
  }
  /** Resets the paste/drop binding guard so a re-created editor rebinds cleanly. */
  ngOnDestroy(): void {
    this.listenersAdded = false;
  }

  /**
   * Resolves an attachment to a secure URL and opens it in a new tab.
   *
   * @param fileName Stored name of the attachment to download.
   */
  downloadAttachment(fileName: string) {
    this.fileAttachmentService.donwloadFile(fileName).subscribe({
      next: (response) => {
        if (response?.secure_url) {
        window.open(response.secure_url, '_blank');
      } else {
        alert('Download link not available.');
      }
      },
      error: (error) => {
        console.error(error)
      }
    })
  }

  /**
   * Uploads an image chosen through the editor toolbar and inserts it at the caret.
   *
   * What goes into the editor is a local `blob:` preview of the picked file, so
   * the writer can see what they inserted; the storage account the upload returns
   * refuses anonymous reads, so that URL cannot be displayed while composing. The
   * upload URL rides along in `data-send-src` and `TicketService` swaps it back
   * into `src` on the way out, so only the uploaded URL ever leaves the browser.
   *
   * The editor's own insert is cancelled before any of that: it embeds a second
   * copy of the same file as a `blob:` URL, which dies with the page, so one pick
   * produced two images and the blob one broke on the next load. Cancelling also
   * stops the dialog closing itself, so close it here.
   *
   * @param args Syncfusion image-select event; the file is read from `filesData[0]`.
   */
  onImageSelected(args: any): void {
    args.cancel = true;
    this.rteObj.closeDialog(DialogType.InsertImage);

    const file = args.filesData[0].rawFile;

    const authToken = sessionStorage.getItem('auth_token');
    if (!authToken) {
      console.error('Missing auth token');
      return;
    }

    const formData = new FormData();
    formData.append('files', file);

    const headers = new HttpHeaders({
      Authorization: `Bearer ${authToken}`
    });

    this.http.post<any>(
      `${this.ticketService.ticketAPI}/inline_files_upload/`,
      formData,
      { headers }
    ).subscribe({
      next: (res) => {
        this.rteObj.focusIn();
        const uploadedUrl = res.files?.[0];
        if (uploadedUrl) {
          const previewUrl = URL.createObjectURL(file);
          const imageHtml = `<img src="${previewUrl}" 
                            data-send-src="${uploadedUrl}" 
                            alt="Uploaded Image" 
                            class="uploaded-image" 
                            style="
                              max-width: 500px !important;
                              min-width: 100px !important;
                              height: auto !important;
                              display: block !important;
                              margin: 10px 0 !important;
                              border-radius: 4px !important;"
                            >`;
        
        this.rteObj.executeCommand('insertHTML', imageHtml);

          // this.rteObj.executeCommand('insertImage', {
          //   url: uploadedUrl,
          //   width: {
          //     minWidth: '100px',
          //     maxWidth: '500px'
          //   },
          //   height: {
          //     minHeight: '100px',
          //     maxHeight: '500px'
          //   },
          //   altText: ''
          // });
        } else {
          console.error('Upload succeeded but no image URL returned.');
        }
      },
      error: (err) => {
        console.error('Image upload failed:', err);
      }
    });
  }

  /**
   * Binds paste and drag-drop image handling onto the editor content area.
   *
   * Guarded by a flag on the element itself, because the observer in
   * {@link onRteCreated} re-runs this whenever the editor re-renders its content.
   */
  private bindPasteAndDropListeners(): void {
    const rteContent = this.rteObj.element.querySelector('.e-rte-content') as HTMLElement;
    if (!rteContent) return;

    // Avoid duplicate bindings
    if ((rteContent as any)._listenersBound) return;
    (rteContent as any)._listenersBound = true;

    rteContent.addEventListener('paste', (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (items) {
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.type.startsWith('image')) {
            const file = item.getAsFile();
            if (file) {
              event.preventDefault();
              this.uploadAndInsertImage(file);
            }
          }
        }
      }
    });

    rteContent.addEventListener('drop', (event: DragEvent) => {
      const files = event.dataTransfer?.files;
        if (files?.length) {
          for (const file of files) {
            if (file.type.startsWith('image/')) {
              event.preventDefault();
              this.uploadAndInsertImage(file);
            }
          }
        }
      });
    }


  /**
   * Wires up image paste and drop once the editor has rendered.
   *
   * The content area is replaced on re-render, so a `MutationObserver` re-binds the
   * handlers rather than binding them a single time.
   */
  onRteCreated(): void {
    setTimeout(() => {
      // if (!this.rteObj || !this.rteObj.element || this.listenersAdded) return;

      if (!this.rteObj || !this.rteObj.element) return;

      const observer = new MutationObserver(() => {
        this.bindPasteAndDropListeners();
      });

      const contentArea = this.rteObj.element.querySelector('.e-rte-content');
      if (contentArea) {
        observer.observe(contentArea, { childList: true, subtree: true });
        this.bindPasteAndDropListeners(); // initial bind
      }

      const rteContent = this.rteObj.element.querySelector('.e-rte-content') as HTMLElement;
      if (!rteContent) return;

      // 📌 Prevent double registration
      this.listenersAdded = true;


      // 📋 Handle Paste
      // rteContent.addEventListener('paste', (event: ClipboardEvent) => {
      //   console.log('Paste event triggered');

      //   const items = event.clipboardData?.items;
      //   console.log('--------------------------')
      //   console.log(items)
      //   if (items) {
      //     for (let i = 0; i < items.length; i++) {
      //     const item = items[0];
      //     console.log('Paste item type:', item.type);
      //     if (item.type.startsWith('image')) {
      //       const file = item.getAsFile();
      //       if (file) {
      //         console.log('Image file found in paste, calling uploadAndInsertImage');
      //         event.preventDefault();
      //         this.uploadAndInsertImage(file);
      //       }
      //     }
      //     }
      //   }
      // });
 
      // 🖱️ Handle Drag-Drop
      // rteContent.addEventListener('drop', (event: DragEvent) => {
      //   const files = event.dataTransfer?.files;
      //   if (files?.length) {
      //     for (const file of files) {
      //       if (file.type.startsWith('image/')) {
      //         event.preventDefault();
      //         this.uploadAndInsertImage(file);
      //       }
      //     }
      //   }
      // });
    }, 100); // Give Angular time to render
  }

  /**
   * Uploads a pasted or dropped image and inserts it at the caret.
   *
   * Shares the markup described on {@link onImageSelected}.
   *
   * @param file Image to upload.
   */
  uploadAndInsertImage(file: File) {
    const authToken = sessionStorage.getItem('auth_token');
    if (!authToken) {
      console.error('Missing auth token');
      return;
    }

    const formData = new FormData();
    formData.append('files', file);

    const headers = new HttpHeaders({
      Authorization: `Bearer ${authToken}`
    });

    this.http.post<any>(
      `${this.ticketService.ticketAPI}/inline_files_upload/`,
      formData,
      { headers }
    ).subscribe({
      next: (res) => {
        this.rteObj.focusIn();
        const uploadedUrl = res?.files?.[0];
        if (uploadedUrl) {
          const previewUrl = URL.createObjectURL(file);
          const imageHtml = `<img src="${previewUrl}" 
                            data-send-src="${uploadedUrl}" 
                            alt="Uploaded Image" 
                            class="uploaded-image" 
                            style="
                              max-width: 500px !important;
                              min-width: 100px !important;
                              height: auto !important;
                              display: block !important;
                              margin: 10px 0 !important;
                              border-radius: 4px !important;"
                            >`;
        
        this.rteObj.executeCommand('insertHTML', imageHtml);
        } else {
          console.error('Upload succeeded but no URL returned.');
        }
      },
      error: (err) => {
        console.error('Image upload failed:', err);
      }
    });
  }
}
